import { errorToast, toast } from "@/v2-ui/toast"
import { useLayerOrderUpdateMutation } from "@/v2-backoffice-client/layer/order/layer.order.mutation"
import { useLayerCategoryOrderUpdateMutation } from "@/v2-backoffice-client/layer/organize/layer.organize.categoryOrder.mutation"
import { LAYER_AUTHORING_STATUSES_QUERY_NAME } from "@/v2-backoffice-client/layer/authoring/layer.authoring.constants"
import type { LayerOrderEntry } from "@/v2-backoffice-client/layer/order/layer.order.utils"

type LayerOrganizeSaveInput = {
  categoryUpdates: LayerOrderEntry[]
  layerUpdates: LayerOrderEntry[]
}

/**
 * Composed save for the organizer's single Save button: writes the category
 * order and the layer order in their two batch mutations (each an atomic
 * batch on the backend), fires the per-mutation error toasts and the one
 * success toast, and reports whether everything saved so the dialog knows
 * when to close.
 */
export function useLayerOrganizeSave() {
  const [saveLayerOrder, layerSaveState] = useLayerOrderUpdateMutation({
    refetchQueries: [LAYER_AUTHORING_STATUSES_QUERY_NAME],
  })
  errorToast(layerSaveState.error, {
    title: "Failed to save the layer order",
  })
  const [saveCategoryOrder, categorySaveState] =
    useLayerCategoryOrderUpdateMutation()
  errorToast(categorySaveState.error, {
    title: "Failed to save the category order",
  })

  async function save(input: LayerOrganizeSaveInput): Promise<boolean> {
    const { categoryUpdates, layerUpdates } = input
    if (categoryUpdates.length > 0) {
      const result = await saveCategoryOrder({
        variables: { input: categoryUpdates },
      })
      if (!result.data) return false
    }
    if (layerUpdates.length > 0) {
      const result = await saveLayerOrder({
        variables: { input: layerUpdates },
      })
      if (!result.data) return false
    }
    toast({
      title: `Saved the new order (${categoryUpdates.length} categor${categoryUpdates.length === 1 ? "y" : "ies"}, ${layerUpdates.length} layer${layerUpdates.length === 1 ? "" : "s"} re-ranked).`,
    })
    return true
  }

  return {
    save,
    isSaving: layerSaveState.loading || categorySaveState.loading,
  }
}
